import React, { useState } from "react";
import { Button, Modal } from "antd";
import CustomDropdownButton from "./CustomDropdownButton";

interface RejectConfig {
	canReject: boolean;
	handleReject: () => void;
	rejectContent: React.ReactNode;
}

interface ApproveRequestDropdownProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: any;
	isSavingOrLoading: boolean;
	handleApprove: () => void;
	handleRequestChanges: () => void;
	approveLabel?: string;
	approveTitle: React.ReactNode;
	requestChangesContent: React.ReactNode;
	canApprove: boolean;
	canRequestChanges: boolean;
	onValidationError?: (error: unknown) => void;
	rejectConfig?: RejectConfig;
}

const ApproveRequestDropdown: React.FC<ApproveRequestDropdownProps> = ({
	form,
	isSavingOrLoading,
	handleApprove,
	handleRequestChanges,
	approveLabel = "Approve",
	approveTitle,
	requestChangesContent,
	canApprove,
	canRequestChanges,
	onValidationError,
	rejectConfig,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

	const canReject = rejectConfig?.canReject ?? false;

	if (!canApprove && !canRequestChanges && !canReject) return null;

	const hasComment = Boolean(form.formData?.request_changes_comment?.trim());
	const hasRejectComment = Boolean(form.formData?.rejection_comment?.trim());

	const handleModalOk = () => {
		handleRequestChanges();
		setIsModalOpen(false);
	};

	const handleModalCancel = () => {
		setIsModalOpen(false);
		form.setFormData({
			...form.formData,
			request_changes_comment: null,
		});
	};

	const handleRejectOk = () => {
		rejectConfig?.handleReject();
		setIsRejectModalOpen(false);
	};

	const handleRejectCancel = () => {
		setIsRejectModalOpen(false);
		form.setFormData({
			...form.formData,
			rejection_comment: null,
		});
	};

	// Build dropdown items based on permissions
	const dropdownItems = [];
	if (canRequestChanges) {
		dropdownItems.push({
			key: "request-changes",
			label: "Request changes",
			onClick: () => setIsModalOpen(true),
		});
	}
	if (canReject) {
		dropdownItems.push({
			key: "reject",
			label: "Reject",
			onClick: () => setIsRejectModalOpen(true),
			danger: true,
		});
	}

	// Only request changes or reject, no approve
	if (!canApprove && (canRequestChanges || canReject)) {
		return (
			<>
				{canRequestChanges && (
					<Button
						type="default"
						disabled={isSavingOrLoading}
						onClick={() => setIsModalOpen(true)}
					>
						Request changes
					</Button>
				)}
				{canReject && (
					<Button
						type="default"
						disabled={isSavingOrLoading}
						onClick={() => setIsRejectModalOpen(true)}
					>
						Reject
					</Button>
				)}
				<Modal
					title="Request Changes"
					open={isModalOpen}
					onOk={handleModalOk}
					onCancel={handleModalCancel}
					okText="Confirm"
					okButtonProps={{ disabled: isSavingOrLoading || !hasComment }}
					cancelButtonProps={{ disabled: isSavingOrLoading }}
				>
					{requestChangesContent}
				</Modal>
				<Modal
					title="Reject Report"
					open={isRejectModalOpen}
					onOk={handleRejectOk}
					onCancel={handleRejectCancel}
					okText="Confirm"
					okButtonProps={{
						disabled: isSavingOrLoading || !hasRejectComment,
					}}
					cancelButtonProps={{ disabled: isSavingOrLoading }}
				>
					{rejectConfig?.rejectContent}
				</Modal>
			</>
		);
	}

	// Only approve, no dropdown items
	if (canApprove && dropdownItems.length === 0) {
		return (
			<CustomDropdownButton
				mainAction={{
					label: approveLabel,
					onClick: handleApprove,
					form: form,
					confirmTitle: approveTitle,
					loading: isSavingOrLoading,
					onValidationError,
				}}
				dropdownItems={[]}
				hideDropdown={true}
			/>
		);
	}

	// Both approve and dropdown items (request changes and/or reject)
	return (
		<>
			<CustomDropdownButton
				mainAction={{
					label: approveLabel,
					onClick: handleApprove,
					form: form,
					confirmTitle: approveTitle,
					loading: isSavingOrLoading,
					onValidationError,
				}}
				dropdownItems={dropdownItems}
			/>
			<Modal
				title="Request Changes"
				open={isModalOpen}
				onOk={handleModalOk}
				onCancel={handleModalCancel}
				okText="Confirm"
				okButtonProps={{ disabled: isSavingOrLoading || !hasComment }}
				cancelButtonProps={{ disabled: isSavingOrLoading }}
			>
				{requestChangesContent}
			</Modal>
			<Modal
				title="Reject Report"
				open={isRejectModalOpen}
				onOk={handleRejectOk}
				onCancel={handleRejectCancel}
				okText="Confirm"
				okButtonProps={{
					disabled: isSavingOrLoading || !hasRejectComment,
				}}
				cancelButtonProps={{ disabled: isSavingOrLoading }}
			>
				{rejectConfig?.rejectContent}
			</Modal>
		</>
	);
};

export default ApproveRequestDropdown;
